import { ref } from "vue";

// AI-шумодав (ворклет + движок из public/noise) работает строго на 48кГц: он
// не делает ресемплинг, а размеры кадров считает из расчёта 48кГц. Это требование относится
// ТОЛЬКО к микрофонному контексту (исходящая обработка). Комнатный контекст
// (webAudioMix) используется лишь для GainNode-микширования входящих и 48кГц
// не требует — поэтому он создаётся без фиксации частоты (дефолт устройства).
export const AUDIO_CONTEXT_SAMPLE_RATE = 48000;

type AudioContextOptions = { sampleRate?: number };

function createRawAudioContext(options: AudioContextOptions): AudioContext {
  const AudioContextCtor =
    typeof window !== "undefined" &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ((window as any).AudioContext || (window as any).webkitAudioContext);
  if (!AudioContextCtor) {
    throw new Error("AudioContext is not supported in this environment");
  }
  try {
    return options.sampleRate
      ? new AudioContextCtor({
          latencyHint: "interactive",
          sampleRate: options.sampleRate,
        })
      : new AudioContextCtor({ latencyHint: "interactive" });
  } catch (err) {
    // Некоторые окружения не умеют фиксированную частоту — откат на дефолт.
    if (options.sampleRate) {
      console.warn(
        `AudioContext on ${options.sampleRate}Hz unavailable, using default`,
        err,
      );
    }
    return new AudioContextCtor({ latencyHint: "interactive" });
  }
}

// Отслеживаемые контексты: сейчас их два (комнатный + микрофонный).
const trackedContexts: AudioContext[] = [];

// ---------------------------------------------------------------------------
// Страховки от "зависшего" suspended (iOS/Safari, автоплей-политики):
//
// A. Постоянный жестовый предохранитель. Пока контекст в "suspended", первый
//    же pointerdown/keydown/click будит его. Живёт всю сессию и НЕ снимается
//    после первого running: iOS суспендит работающий контекст посреди звонка
//    (входящий звонок, Siri, переключение устройств, фон), и тогда нужен новый
//    жест, чтобы поднять его снова.
// B. statechange-watchdog. При каждом переходе в "suspended" делаем серию
//    попыток resume с backoff — часть суспендов снимается без жеста (Chrome
//    после прерываний/смены устройств), но не мгновенно. Попытки ограничены;
//    если жест так и не поступил — дальше ждём жестовый предохранитель (A).
// ---------------------------------------------------------------------------

// Поколение для отмены устаревших цепочек backoff после running/closed.
const resumeGeneration = new Map<AudioContext, number>();
// Первая попытка сразу, дальше с нарастающей задержкой.
const RESUME_RETRY_DELAYS = [0, 250, 1000, 3000, 8000];

function startResumeWatchdog(audioContext: AudioContext): void {
  const generation = (resumeGeneration.get(audioContext) ?? 0) + 1;
  resumeGeneration.set(audioContext, generation);
  let attempt = 0;
  const tryResume = () => {
    if (audioContext.state !== "suspended") return;
    if (resumeGeneration.get(audioContext) !== generation) return;
    if (attempt >= RESUME_RETRY_DELAYS.length) return;
    const delay = RESUME_RETRY_DELAYS[attempt++];
    setTimeout(() => {
      if (audioContext.state !== "suspended") return;
      if (resumeGeneration.get(audioContext) !== generation) return;
      audioContext.resume().catch(() => {});
      tryResume();
    }, delay);
  };
  tryResume();
}

function resumeAllSuspended(): void {
  for (const ctx of trackedContexts) {
    if (ctx.state === "suspended") {
      ctx.resume().catch(() => {});
    }
  }
}

// Свежая серия попыток для всех спящих контекстов. Нужна на возврате во
// вкладку: watchdog, запущенный в фоне, iOS мог заморозить или он уже исчерпан,
// а один внежестовый resume() на iOS часто отклоняют.
function resumeAllWithWatchdog(): void {
  for (const ctx of trackedContexts) {
    if (ctx.state === "suspended") {
      startResumeWatchdog(ctx);
    }
  }
}

// Следим за контекстом весь его жизненный цикл: на каждый уход в "suspended"
// запускаем watchdog (B), на running/closed — отменяем текущую серию попыток.
function attachResumeSafetyNet(audioContext: AudioContext): void {
  trackedContexts.push(audioContext);
  audioContext.addEventListener("statechange", () => {
    if (audioContext.state === "suspended") {
      startResumeWatchdog(audioContext);
    } else if (
      audioContext.state === "running" ||
      audioContext.state === "closed"
    ) {
      resumeGeneration.set(
        audioContext,
        (resumeGeneration.get(audioContext) ?? 0) + 1,
      );
    }
  });
}

// Постоянный жестовый предохранитель на весь документ (регистрируется один раз).
let gestureSafetyNetAttached = false;
function attachGestureSafetyNet(): void {
  if (gestureSafetyNetAttached) return;
  gestureSafetyNetAttached = true;
  document.addEventListener("pointerdown", resumeAllSuspended);
  document.addEventListener("click", resumeAllSuspended);
  document.addEventListener("keydown", (event) => {
    if (!event.repeat) resumeAllSuspended();
  });
}

export function getNewAudioContext(
  options: AudioContextOptions = {},
): AudioContext | undefined {
  try {
    const ctx = createRawAudioContext(options);
    attachResumeSafetyNet(ctx);
    // Первичный suspended (iOS/автоплей до первого жеста): запускаем серию
    // попыток, которая выполнится при первом же жесте (или без жеста, где
    // политика браузера это разрешает).
    if (ctx.state === "suspended") {
      startResumeWatchdog(ctx);
    }
    return ctx;
  } catch (err) {
    console.warn("Failed to create AudioContext", err);
    return undefined;
  }
}

// Комнатный контекст для входящих (webAudioMix). Без фиксации sampleRate:
// частота по умолчанию устройства, 48кГц для него не требуется.
export const audioContext = getNewAudioContext();

// Контекст только для исходящей обработки микрофона (VAD, уровень сигнала,
// AI-шумоподавление) — строго 48кГц. Отдельно от `audioContext`,
// который LiveKit использует для воспроизведения входящих треков (webAudioMix).
export const microphoneAudioContext = getNewAudioContext({
  sampleRate: AUDIO_CONTEXT_SAMPLE_RATE,
});

// Жестовый предохранитель (A) вешаем на документ один раз за сессию — после
// того, как оба контекста созданы и добавлены в trackedContexts.
if (typeof document !== "undefined") {
  attachGestureSafetyNet();
}

export function getMicProcessingContext(): AudioContext | undefined {
  return microphoneAudioContext;
}

// Резюмим оба контекста. 
export async function resumeAudioContext(): Promise<boolean> {
  let success = true;
  for (const ctx of [audioContext, microphoneAudioContext]) {
    if (!ctx || ctx.state !== "suspended") continue;
    try {
      await ctx.resume();
    } catch (err) {
      console.error("Failed to resume AudioContext", err);
      success = false;
    }
  }
  return success;
}

// Гарантируем, что контекст вышел в "running", повторив resume с ограниченным
// числом попыток. Нужно для случаев, когда запуск совпадает с длительной
// инициализацией (например, сетевой загрузкой модели шумодава) и resume по
// жесту мог быть проигнорирован браузером.
export async function ensureAudioContextRunning(
  audioContext: AudioContext | undefined,
  maxAttempts: number = 3,
): Promise<void> {
  if (!audioContext) return;
  // Читаем state через функцию, чтобы TS не сужал тип свойства между
  // асинхронными вызовами (state меняется в рантайме).
  const isRunning = () => audioContext!.state === "running";
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (isRunning()) return;
    try {
      await audioContext.resume();
    } catch (err) {
      console.error("Failed to resume AudioContext", err);
    }
    if (isRunning()) return;
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
}

export const VadModuleError = ref(false);

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) resumeAllWithWatchdog();
  });
}

// VAD-процессор нужен только исходящей обработке микрофона, которая живёт в
// microphoneAudioContext (AI-шумодав регистрирует свой модуль сам).
let loaded = false;
export async function loadAudioModules() {
  const ctx = microphoneAudioContext;
  if (!ctx || loaded) return;
  const vadProcessorUrl = new URL("../utils/vad-processor.js", import.meta.url)
    .href;
  await ctx.audioWorklet.addModule(vadProcessorUrl).catch(() => {
    VadModuleError.value = true;
  });
  loaded = true;
}
